/**
 * KnowledgeIngestPanel — vibe-designer 1.172.0 Phase 5α.
 *
 * Top-of-Overview ingest UI. Phase 5α scope:
 *   - text-paste textarea + source-name input
 *   - target-services checkbox group (mind/cortex/obsidian active; brain/
 *     cognition/sight DISABLED with explanatory titles per OQ-5.1/5.2/SIGHT-defer)
 *   - collection dropdown (MIND-specific routing)
 *   - submit → POST /api/knowledge/ingest → renders per-service result cards
 *
 * File upload + image batch deferred to Phase 5b/6. Realtime polling-based
 * job status: Phase 5α ships SYNCHRONOUS ingest (POST blocks until all
 * target services return); the job cache supports the GET /ingest/{job_id}
 * /status route for future async expansion.
 *
 * Honest fallback (NEVER silent empty):
 *   - per-service status=failed → red banner with verbatim error
 *   - per-service status=skipped → amber banner naming the gap
 *   - per-service status=partial → yellow banner with truncation warning
 *   - per-service status=completed → green badge with chunks_created count
 *   - network error (Promise rejection) → red banner at panel level
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type ChangeEvent } from 'react';
import clsx from 'clsx';
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Info, Upload, FolderUp, FileText } from 'lucide-react';
import { useKnowledgeStore } from '@/stores/knowledgeStore';
import { PillarChips } from './PillarChips';
import { SERVICE_META } from './serviceMeta';
import {
  classifyFolderFiles,
  FOLDER_MAX_FILES,
  FOLDER_MAX_TOTAL_BYTES,
} from '@/utils/folderClassify';

// 2026-06-28 — accept list for the individual document-file picker. Mirrors
// the classifier's supported extensions (folderClassify.ts) PLUS .pdf, so the
// OS file dialog lets the user SELECT these (the image picker's accept=image/*
// greyed them out). The classifier is still the source of truth for routing +
// honest-skip of anything that slips through.
const DOC_PICKER_ACCEPT = [
  '.md', '.markdown', '.txt', '.py', '.ts', '.tsx', '.js', '.json',
  '.yaml', '.yml', '.toml', '.sh', '.go', '.rs', '.pdf',
  '.png', '.jpg', '.jpeg', '.gif',
].join(',');
import type {
  IngestServiceResult,
  IngestStatus,
  KnowledgeServiceStats,
} from '@/services/knowledgeApi';
import { getOcrHealth } from '@/services/knowledgeApi';

type ServiceName = KnowledgeServiceStats['service'];

const SERVICE_LABELS: Record<ServiceName, string> = {
  mind: 'THE MIND',
  cortex: 'THE CORTEX',
  brain: 'THE BRAIN',
  sight: 'THE SIGHT',
  cognition: 'THE COGNITION',
  obsidian: 'OBSIDIAN',
};

// Phase 5α: MIND + CORTEX + OBSIDIAN active. BRAIN + COGNITION + SIGHT deferred.
// 1.176.0 Phase 5γ.1: SIGHT text write reversed from honest-skip to active.
// 1.197.0 Phase 8.4: COGNITION ingest activated (cognee /api/v1/add via the
// FastAPI-users Bearer token wired by Phase 8.1). BRAIN remains deferred until
// the Ollama/Infinity pivot lands.
const ACTIVE_SERVICES: ServiceName[] = ['mind', 'cortex', 'obsidian', 'sight', 'cognition'];
const DEFERRED_TITLES: Record<ServiceName, string> = {
  mind: '',
  cortex: '',
  brain: 'BRAIN ingest blocked (Ollama 9100 down or BRAIN config needs Infinity pivot)',
  sight: '',
  cognition: '',
  obsidian: '',
};

const COLLECTIONS = ['docs', 'configs', 'code', 'sessions'] as const;

export function KnowledgeIngestPanel() {
  // Phase 9.0 Surface B — vibe:focus-panel listener for voice-driven focus.
  const sectionRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const ev = e as CustomEvent<{ panel?: string }>;
      if (ev.detail?.panel !== 'knowledge-ingest') return;
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('vibe:focus-panel', handler);
    return () => window.removeEventListener('vibe:focus-panel', handler);
  }, []);

  const ingestResponse = useKnowledgeStore((s) => s.ingestResponse);
  const isIngesting = useKnowledgeStore((s) => s.isIngesting);
  const ingestError = useKnowledgeStore((s) => s.ingestError);
  const runIngest = useKnowledgeStore((s) => s.runIngest);
  const runIngestUrl = useKnowledgeStore((s) => s.runIngestUrl);
  const runIngestRepo = useKnowledgeStore((s) => s.runIngestRepo);
  // 1.194.0 Phase 7.8 — folder ingest store wiring.
  const isIngestingFolder = useKnowledgeStore((s) => s.isIngestingFolder);
  const folderIngestProgress = useKnowledgeStore((s) => s.folderIngestProgress);
  const folderIngestResults = useKnowledgeStore((s) => s.folderIngestResults);
  const runIngestFolder = useKnowledgeStore((s) => s.runIngestFolder);

  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [targets, setTargets] = useState<Set<ServiceName>>(new Set(['mind']));
  const [collection, setCollection] = useState<(typeof COLLECTIONS)[number]>('docs');
  // 1.184.0 Phase 7.1 — capture pillar at ingest time. Forwarded via
  // metadata.pillar dict (NOT a top-level IngestRequest field). Only
  // MIND persists it to a dedicated `pillar` column; non-MIND ignores.
  const [pillar, setPillar] = useState<string | null>(null);

  // 1.179.0 Phase 6.2.1 — code-repo ingest state. Public HTTPS git URL +
  // optional branch. Backend SSRF guard rejects internal Forgejo + RFC1918
  // by design (mirror of Phase 6.1 URL ingest scope).
  const [gitUrl, setGitUrl] = useState('');
  const [branch, setBranch] = useState('');

  // 1.178.0 Phase 5γ.2 — image upload state. R1 C3 LOCK: PNG/JPEG/GIF only;
  // WebP rejected by backend so leaving it out of the accept list per
  // honest-scope reduction. Frontend 5MB cap matches backend's
  // _SIGHT_MAX_IMAGE_BYTES (Concern 3).
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imageProgress, setImageProgress] = useState<{ done: number; total: number; error: string | null }>({
    done: 0, total: 0, error: null,
  });

  // 1.194.0 Phase 7.8 — folder upload. Raw File[] from the
  // <input webkitdirectory> picker. Classification + counts + cap check
  // computed via useMemo from the raw list.
  const [folderFiles, setFolderFiles] = useState<File[]>([]);
  const classifiedFolderFiles = useMemo(
    () => (folderFiles.length > 0 ? classifyFolderFiles(folderFiles) : []),
    [folderFiles],
  );
  const folderSupported = useMemo(
    () => classifiedFolderFiles.filter((c) => c.targetService !== null),
    [classifiedFolderFiles],
  );
  const folderSkippedCount = classifiedFolderFiles.length - folderSupported.length;
  const folderMindCount = folderSupported.filter((c) => c.targetService === 'mind').length;
  const folderSightCount = folderSupported.filter((c) => c.targetService === 'sight').length;
  const folderTotalBytes = folderSupported.reduce((s, c) => s + c.file.size, 0);
  const folderCapError = useMemo<string | null>(() => {
    if (folderSupported.length > FOLDER_MAX_FILES) {
      return `folder exceeds ${FOLDER_MAX_FILES} files cap (${folderSupported.length} supported)`;
    }
    if (folderTotalBytes > FOLDER_MAX_TOTAL_BYTES) {
      return `folder exceeds 100MB total bytes cap (${(folderTotalBytes / 1024 / 1024).toFixed(1)}MB)`;
    }
    return null;
  }, [folderSupported.length, folderTotalBytes]);

  // 2026-06-28 — individual document-file picker. Same per-file classifier +
  // runIngestFolder execution as the folder picker (text/md/code → MIND text,
  // PDF → MIND-via-server-extract, images → SIGHT), but the user selects
  // individual files instead of a whole directory.
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const classifiedDocFiles = useMemo(
    () => (docFiles.length > 0 ? classifyFolderFiles(docFiles) : []),
    [docFiles],
  );
  const docSupported = useMemo(
    () => classifiedDocFiles.filter((c) => c.targetService !== null),
    [classifiedDocFiles],
  );
  const docSkippedCount = classifiedDocFiles.length - docSupported.length;
  // Feature B (2026-06-28) — scanned-PDF async OCR UX. Set at submit time
  // (classifiedDocFiles is cleared right after dispatch), so the hint + queue
  // poll persist for the whole in-flight batch.
  const [batchHasPdf, setBatchHasPdf] = useState(false);
  const [ocrQueueDepth, setOcrQueueDepth] = useState<number | null>(null);
  useEffect(() => {
    if (!(isIngestingFolder && batchHasPdf)) {
      setOcrQueueDepth(null);
      if (!isIngestingFolder) setBatchHasPdf(false);
      return;
    }
    let cancelled = false;
    const poll = () => {
      void getOcrHealth()
        .then((h) => { if (!cancelled) setOcrQueueDepth(h.available ? h.queue_depth : null); })
        .catch(() => { if (!cancelled) setOcrQueueDepth(null); });
    };
    poll();
    const id = window.setInterval(poll, 3000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [isIngestingFolder, batchHasPdf]);
  const docMindCount = docSupported.filter((c) => c.targetService === 'mind').length;
  const docSightCount = docSupported.filter((c) => c.targetService === 'sight').length;
  const docTotalBytes = docSupported.reduce((s, c) => s + c.file.size, 0);
  const docCapError = useMemo<string | null>(() => {
    if (docSupported.length > FOLDER_MAX_FILES) {
      return `selection exceeds ${FOLDER_MAX_FILES} files cap (${docSupported.length} supported)`;
    }
    if (docTotalBytes > FOLDER_MAX_TOTAL_BYTES) {
      return `selection exceeds 100MB total bytes cap (${(docTotalBytes / 1024 / 1024).toFixed(1)}MB)`;
    }
    return null;
  }, [docSupported.length, docTotalBytes]);

  // 1.192.0 Phase 7.7 (§5.4 issue 3): race-safe image-mode target pivot.
  // When the user adds the first image, snapshot the current targets and
  // switch to {sight} so the Targets checkboxes match what the SPA
  // submitImageBatch hard-codes (target_services=['sight']). When the user
  // clears the images, restore the snapshot.
  //
  // Race avoidance: the pivot logic lives in `onImageFilesChange` AND in the
  // Clear button handler — NOT in a `useEffect([imageFiles.length])`. Effects
  // would race with manual checkbox clicks because state updates settle out
  // of order. Two refs (no rerender):
  //   - previousTargetsRef: snapshot taken BEFORE pivot
  //   - imageModePivotActiveRef: true after pivot, false if user manually
  //     toggles a checkbox (so Clear preserves user intent on restore)
  const previousTargetsRef = useRef<Set<ServiceName> | null>(null);
  const imageModePivotActiveRef = useRef<boolean>(false);

  const toggleTarget = (svc: ServiceName) => {
    // R1 issue 3 invariant — manual toggle while pivot is active disables
    // the auto-restore so the user's choice survives the Clear handler.
    if (imageModePivotActiveRef.current) {
      imageModePivotActiveRef.current = false;
    }
    setTargets((prev) => {
      const next = new Set(prev);
      if (next.has(svc)) next.delete(svc);
      else next.add(svc);
      return next;
    });
  };

  // 1.177.0 Phase 6.1 — URL ingest path. URL field is OPTIONAL; when
  // non-empty it takes precedence (text + source_name inputs ignored).
  // URL submit dispatches to the dedicated /api/knowledge/ingest/url
  // endpoint which does fetch + SSRF guard + text extract server-side.
  const urlMode = url.trim().length > 0;
  // 1.178.0 Phase 5γ.2 — image upload mode takes precedence over both URL and text.
  const imageMode = imageFiles.length > 0;
  // 1.179.0 Phase 6.2.1 — code-repo ingest mode.
  // 1.194.0 Phase 7.8 — folder upload mode. Precedence:
  // imageMode > folderMode > repoMode > urlMode > textMode.
  const repoMode = gitUrl.trim().length > 0;
  const folderMode = classifiedFolderFiles.length > 0;
  // 2026-06-28 — document-file picker mode. Precedence sits between folder and
  // repo: imageMode > folderMode > docMode > repoMode > urlMode > textMode.
  const docMode = classifiedDocFiles.length > 0;
  // R1 C1 lock: outer guard widens to !isIngesting && !isIngestingFolder so
  // an in-flight folder/doc batch keeps the submit button disabled across modes
  // (folder + doc picker share runIngestFolder + isIngestingFolder state).
  const canSubmit =
    !isIngesting && !isIngestingFolder &&
    (imageMode
      ? imageFiles.length > 0
      : folderMode
        ? folderSupported.length > 0 && !folderCapError
        : docMode
          ? docSupported.length > 0 && !docCapError
          : repoMode
            ? targets.size > 0
            : urlMode
              ? targets.size > 0
              : targets.size > 0 && text.trim().length > 0 && sourceName.trim().length > 0);

  const _SIGHT_MAX_IMAGE_BYTES_CLIENT = 5 * 1024 * 1024;
  const _ACCEPTED_IMAGE_MIMES = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];

  const onImageFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Client-side validation mirrors backend cap (defense-in-depth).
    const errors: string[] = [];
    const accepted: File[] = [];
    for (const f of files) {
      if (!_ACCEPTED_IMAGE_MIMES.includes(f.type)) {
        errors.push(`${f.name}: unsupported format ${f.type || '(unknown)'}`);
        continue;
      }
      if (f.size > _SIGHT_MAX_IMAGE_BYTES_CLIENT) {
        errors.push(`${f.name}: ${(f.size / 1024 / 1024).toFixed(1)}MB exceeds 5MB cap`);
        continue;
      }
      accepted.push(f);
    }
    // 1.192.0 Phase 7.7 (§5.4 issue 3): perform target pivot here, NOT in a
    // useEffect on imageFiles.length — directly tied to the user action so
    // there is no race with manual checkbox clicks. Only pivot on the
    // [] → [files] transition; subsequent picker uses while images already
    // present do NOT re-pivot (preserves any manual targets override).
    const wasEmpty = imageFiles.length === 0;
    if (wasEmpty && accepted.length > 0) {
      previousTargetsRef.current = new Set(targets);
      imageModePivotActiveRef.current = true;
      setTargets(new Set(['sight']));
    }
    setImageFiles(accepted);
    setImageProgress({ done: 0, total: accepted.length, error: errors.join('; ') || null });
  };

  // 1.192.0 Phase 7.7 (§5.4 issue 3): Clear handler. Restores the pre-image
  // targets snapshot ONLY if the user did not manually override checkboxes
  // during imageMode (which would have flipped imageModePivotActiveRef=false
  // via toggleTarget). Always clears the imageFiles + progress state.
  const clearImageFiles = () => {
    if (imageModePivotActiveRef.current && previousTargetsRef.current) {
      setTargets(previousTargetsRef.current);
    }
    previousTargetsRef.current = null;
    imageModePivotActiveRef.current = false;
    setImageFiles([]);
    setImageProgress({ done: 0, total: 0, error: null });
  };

  const fileToDataUri = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });

  const submitImageBatch = async () => {
    // Sequential per-file ingest. Parallelism deferred to Phase 5γ.2.1
    // (vLLM batch-size + GPU OOM risk). R1 acknowledged-risk #8.
    setImageProgress({ done: 0, total: imageFiles.length, error: null });
    for (let i = 0; i < imageFiles.length; i++) {
      const file = imageFiles[i];
      try {
        const dataUri = await fileToDataUri(file);
        // SIGHT-only — orchestrator drops other targets anyway, but the
        // SPA is explicit so the user sees only "sight" entries on a
        // multi-target preset.
        await useKnowledgeStore.getState().runIngest({
          content: dataUri,
          source_name: file.name,
          target_services: ['sight'],
          collection,
          content_type: 'image',
        });
      } catch (e: unknown) {
        setImageProgress((p) => ({
          ...p,
          done: i + 1,
          error: (p.error ? p.error + '; ' : '') + `${file.name}: ${e instanceof Error ? e.message : String(e)}`,
        }));
        continue;
      }
      setImageProgress((p) => ({ ...p, done: i + 1 }));
    }
    // 1.192.0 Phase 7.7 — use the same restore handler as the Clear button
    // so the user's pre-image targets come back after a successful batch.
    clearImageFiles();
  };

  // 1.194.0 Phase 7.8 — folder picker handlers. No target pivot here
  // (per-file classifier handles routing). Clear button is disabled during
  // an in-flight batch (R1 C5 lock — no cancellation feature).
  const onFolderFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFolderFiles(Array.from(e.target.files || []));
  };

  const clearFolderFiles = () => {
    setFolderFiles([]);
  };

  // 2026-06-28 — document-file picker handlers (mirror folder; no target pivot,
  // per-file classifier handles routing including PDF → server-side extract).
  const onDocFilesChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDocFiles(Array.from(e.target.files || []));
  };

  const clearDocFiles = () => {
    setDocFiles([]);
  };

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (imageMode) {
      void submitImageBatch();
      return;
    }
    if (folderMode) {
      // 2026-06-28 fix: honor the Targets checkboxes for text-capable files —
      // pass `textTargets` (was omitted, so every folder file routed to MIND
      // only even with CORTEX/COGNITION checked). Images still route to SIGHT
      // (ingestOneFolderFile forces image→SIGHT regardless of textTargets).
      setBatchHasPdf(classifiedFolderFiles.some((c) => c.contentType === 'pdf'));
      void runIngestFolder(classifiedFolderFiles, {
        collection,
        pillar,
        textTargets: Array.from(targets),
      });
      return;
    }
    if (docMode) {
      // Same execution path as folder upload: per-file classify → runIngestFolder
      // (text/md/code read client-side, PDF uploaded for server-side extract,
      // images → SIGHT). Both the doc-file picker and the folder picker honor the
      // Targets checkboxes for text-capable files (so a PDF can be injected into
      // MIND + CORTEX + OBSIDIAN + COGNITION at once); images still route to
      // SIGHT. Clears the picker after dispatch.
      setBatchHasPdf(classifiedDocFiles.some((c) => c.contentType === 'pdf'));
      void runIngestFolder(classifiedDocFiles, {
        collection,
        pillar,
        textTargets: Array.from(targets),
      });
      clearDocFiles();
      return;
    }
    const metadata = pillar ? { pillar } : {};
    if (repoMode) {
      void runIngestRepo({
        git_url: gitUrl.trim(),
        branch: branch.trim() || undefined,
        target_services: Array.from(targets),
        collection,
        metadata,
      });
      return;
    }
    if (urlMode) {
      void runIngestUrl({
        url: url.trim(),
        target_services: Array.from(targets),
        collection,
        metadata,
      });
      return;
    }
    void runIngest({
      content: text,
      source_name: sourceName.trim(),
      target_services: Array.from(targets),
      collection,
      content_type: 'markdown',
      ...(pillar ? { metadata } : {}),
    });
  };

  return (
    <section
      ref={sectionRef}
      data-testid="knowledge-ingest-section"
    >
      <div className="mb-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-vibe-text-secondary">
            Pillar
          </span>
          <span
            data-testid="knowledge-ingest-pillar-help"
            title="Pillars tag content for MIND's brand system. Other targets ignore the pillar."
            aria-label="pillar help"
            className="text-vibe-text-tertiary cursor-help"
          >
            <Info className="w-3 h-3" />
          </span>
        </div>
        <PillarChips value={pillar} onChange={setPillar} />
        <div className="text-[11px] text-vibe-text-tertiary mt-1">
          (applies to MIND only — other targets discard pillar metadata)
        </div>
      </div>
      <form onSubmit={onSubmit} className="space-y-2">
        {/* Phase 5γ.2: OPTIONAL image upload. When files selected, submit
            does sequential per-file SIGHT ingest with content_type='image'.
            Other inputs (URL, source_name, text) disabled in imageMode.
            Backend orchestrator filter drops non-SIGHT targets server-side. */}
        <div className="flex items-center gap-2">
          <label
            data-testid="knowledge-ingest-image-picker-label"
            className="px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text-secondary hover:bg-vibe-bg-tertiary cursor-pointer flex items-center gap-2 flex-1"
          >
            <Upload className="w-4 h-4" />
            <span className="truncate">
              {imageMode
                ? `${imageFiles.length} image${imageFiles.length === 1 ? '' : 's'} ready (SIGHT only)`
                : 'Upload images to SIGHT — PNG/JPEG/GIF up to 5MB each'}
            </span>
            <input
              data-testid="knowledge-ingest-image-input"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/jpg,image/gif"
              onChange={onImageFilesChange}
              disabled={isIngesting}
              className="hidden"
            />
          </label>
          {imageMode && (
            <button
              data-testid="knowledge-ingest-image-clear"
              type="button"
              onClick={clearImageFiles}
              disabled={isIngesting}
              className="px-2 py-1.5 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
        {imageProgress.error && (
          <div
            data-testid="knowledge-ingest-image-error"
            className="rounded border border-vibe-warning/30 bg-vibe-warning/10 p-2 text-xs text-vibe-warning"
          >
            {imageProgress.error}
          </div>
        )}
        {imageMode && imageProgress.total > 0 && imageProgress.done > 0 && (
          <div className="text-xs text-vibe-text-secondary">
            Image batch: {imageProgress.done}/{imageProgress.total} processed
          </div>
        )}

        {/* Phase 7.8: optional folder upload. <input webkitdirectory> walks
            the picked directory; classifyFolderFiles routes by extension to
            MIND (text/markdown/code) or SIGHT (PNG/JPEG/GIF) per file, or
            honest-skips unsupported types in the preview banner. */}
        <div className="flex items-center gap-2">
          <label
            data-testid="knowledge-ingest-folder-picker-label"
            className="px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text-secondary hover:bg-vibe-bg-tertiary cursor-pointer flex items-center gap-2 flex-1"
          >
            <FolderUp className="w-4 h-4" />
            <span className="truncate">
              {folderMode
                ? `${folderSupported.length} files (${folderMindCount} → MIND, ${folderSightCount} → SIGHT${
                    folderSkippedCount > 0 ? `, ${folderSkippedCount} skipped` : ''
                  })`
                : 'OR upload a folder (mixed text + images, recursive)'}
            </span>
            <input
              data-testid="knowledge-ingest-folder-input"
              type="file"
              multiple
              // webkitdirectory is a non-standard but well-supported attribute;
              // not in lib.dom.d.ts so the cast escape-hatches the prop.
              {...({ webkitdirectory: '' } as Record<string, string>)}
              onChange={onFolderFilesChange}
              disabled={isIngesting || isIngestingFolder}
              className="hidden"
            />
          </label>
          {folderMode && (
            <button
              data-testid="knowledge-ingest-folder-clear"
              type="button"
              onClick={clearFolderFiles}
              disabled={isIngestingFolder}
              className="px-2 py-1.5 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
        {folderMode && folderCapError && (
          <div
            data-testid="knowledge-ingest-folder-cap-error"
            className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
          >
            {folderCapError}
          </div>
        )}
        {isIngestingFolder && folderIngestProgress && (
          <div
            data-testid="knowledge-ingest-folder-progress"
            className="text-xs text-vibe-text-secondary"
          >
            Folder batch: {folderIngestProgress.done}/{folderIngestProgress.total} processed
            {folderIngestProgress.errors > 0 ? ` (${folderIngestProgress.errors} errors)` : ''}
          </div>
        )}
        {isIngestingFolder && batchHasPdf && (
          <div
            data-testid="knowledge-ingest-ocr-hint"
            className="flex items-center gap-1.5 text-xs text-vibe-text-secondary"
          >
            <Loader2 className="w-3 h-3 animate-spin text-vibe-accent" />
            <span>
              Scanned PDFs are OCR&apos;d on the GPU node (~20–30s per page)
              {ocrQueueDepth != null && ocrQueueDepth > 1
                ? ` — worker busy, ${ocrQueueDepth} ahead in queue`
                : ''}
            </span>
          </div>
        )}
        {!isIngestingFolder && folderIngestResults && folderIngestResults.length > 0 && (() => {
          // 1.195.0 Phase 7.9 — banner shows succeeded/total + (if any failed)
          // cap-5 list of failing source names with overflow tail.
          const failedList = folderIngestResults.filter((r) => r.status === 'failed');
          const succeeded = folderIngestResults.length - failedList.length;
          const CAP = 5;
          const cappedNames = failedList.slice(0, CAP).map((r) => r.sourceName);
          const overflow = failedList.length > CAP ? ` (and ${failedList.length - CAP} more)` : '';
          return (
            <div
              data-testid="knowledge-ingest-folder-results"
              className="rounded border border-vibe-border bg-vibe-bg p-2 text-xs text-vibe-text-secondary"
            >
              Folder ingest complete: {succeeded} OK / {folderIngestResults.length} total
              {failedList.length > 0 && (
                <div
                  data-testid="knowledge-ingest-folder-errors"
                  className="mt-1 text-vibe-warning"
                >
                  {failedList.length} with errors: {cappedNames.join(', ')}{overflow}
                </div>
              )}
            </div>
          );
        })()}

        {/* 2026-06-28: individual document-file picker. Select one or more
            text/markdown/code/PDF/image files directly (the image picker's
            accept=image/* greyed these out). Same per-file classify +
            runIngestFolder execution as the folder picker; PDFs are uploaded
            for server-side text extraction. */}
        <div className="flex items-center gap-2">
          <label
            data-testid="knowledge-ingest-doc-picker-label"
            className="px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text-secondary hover:bg-vibe-bg-tertiary cursor-pointer flex items-center gap-2 flex-1"
          >
            <FileText className="w-4 h-4" />
            <span className="truncate">
              {docMode
                ? `${docSupported.length} files (${docMindCount} → MIND, ${docSightCount} → SIGHT${
                    docSkippedCount > 0 ? `, ${docSkippedCount} skipped` : ''
                  })`
                : 'OR select files to ingest — text, markdown, code, PDF, images'}
            </span>
            <input
              data-testid="knowledge-ingest-doc-input"
              type="file"
              multiple
              accept={DOC_PICKER_ACCEPT}
              onChange={onDocFilesChange}
              disabled={isIngesting || isIngestingFolder}
              className="hidden"
            />
          </label>
          {docMode && (
            <button
              data-testid="knowledge-ingest-doc-clear"
              type="button"
              onClick={clearDocFiles}
              disabled={isIngestingFolder}
              className="px-2 py-1.5 text-xs rounded text-vibe-text-secondary hover:bg-vibe-bg-tertiary disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
        {docMode && docCapError && (
          <div
            data-testid="knowledge-ingest-doc-cap-error"
            className="rounded border border-vibe-error/30 bg-vibe-error/10 p-2 text-xs text-vibe-error"
          >
            {docCapError}
          </div>
        )}

        {/* Phase 6.1: optional URL input. When non-empty, submit takes
            the URL path instead of the text-paste path. The backend
            handles fetch + SSRF guard + extract + delegate to ingest_now. */}
        <input
          data-testid="knowledge-ingest-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={
            imageMode ? '(URL ingest disabled in image mode)'
            : folderMode ? '(URL ingest disabled in folder mode)'
            : repoMode ? '(URL ingest disabled in repo mode)'
            : 'OR ingest a public URL (https://… — fetched server-side)'
          }
          className="w-full px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary disabled:opacity-50"
          disabled={isIngesting || imageMode || folderMode || docMode || repoMode}
        />
        {/* Phase 6.2.1: optional code-repo input. When non-empty, takes
            precedence over URL and text paths. Backend handles git clone
            + walk + per-file ingest + SSRF guard server-side. Public HTTPS
            only — internal Forgejo rejected by design. */}
        <div className="flex gap-2">
          <input
            data-testid="knowledge-ingest-git-url"
            type="url"
            value={gitUrl}
            onChange={(e) => setGitUrl(e.target.value)}
            placeholder={
              imageMode ? '(repo ingest disabled in image mode)'
              : folderMode ? '(repo ingest disabled in folder mode)'
              : 'OR ingest a public git repo (https://github.com/owner/repo.git)'
            }
            className="flex-1 px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary disabled:opacity-50"
            disabled={isIngesting || imageMode || folderMode || docMode}
          />
          <input
            data-testid="knowledge-ingest-git-branch"
            type="text"
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            placeholder={repoMode ? 'branch (optional)' : '(branch — repo only)'}
            className="w-40 px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary disabled:opacity-50"
            disabled={isIngesting || !repoMode}
          />
        </div>
        <input
          data-testid="knowledge-ingest-source-name"
          type="text"
          value={sourceName}
          onChange={(e) => setSourceName(e.target.value)}
          placeholder={
            imageMode ? '(source name = filename, one per file)'
            : folderMode ? '(source name = per-file webkitRelativePath)'
            : repoMode ? '(source name = repo-ingest/{slug}/{path})'
            : urlMode ? '(source name derived from URL)'
            : 'source name (e.g. uploaded/note.md)'
          }
          className="w-full px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary disabled:opacity-50"
          disabled={isIngesting || urlMode || imageMode || folderMode || docMode || repoMode}
        />
        <textarea
          data-testid="knowledge-ingest-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            imageMode ? '(text disabled in image mode)'
            : folderMode ? '(content read per-file from folder above)'
            : repoMode ? '(content will be cloned from git repo above)'
            : urlMode ? '(content will be fetched from URL above)'
            : 'paste content here (max 20MB)…'
          }
          rows={4}
          className="w-full px-3 py-1.5 text-sm rounded bg-vibe-bg border border-vibe-border text-vibe-text placeholder:text-vibe-text-secondary font-mono disabled:opacity-50"
          disabled={isIngesting || urlMode || imageMode || folderMode || docMode || repoMode}
        />

        <div className="flex flex-wrap items-center gap-2.5">
          <span
            className="flex items-center gap-1.5 text-xs font-medium text-vibe-text-secondary"
          >
            Send to
            <span
              data-testid="knowledge-ingest-targets-help"
              title="Pick which knowledge systems receive this content. A dim dot means that system is offline; images always route to SIGHT."
              aria-label="targets help"
              className="text-vibe-text-tertiary cursor-help"
            >
              <Info className="w-3 h-3" />
            </span>
          </span>
          {(Object.keys(SERVICE_LABELS) as ServiceName[]).map((svc) => {
            const deferred = !ACTIVE_SERVICES.includes(svc);
            const meta = SERVICE_META[svc];
            const checked = targets.has(svc);
            return (
              <label
                key={svc}
                className={clsx(
                  'flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border cursor-pointer transition-colors',
                  checked
                    ? 'border-vibe-border-strong bg-vibe-surface-raised text-vibe-text'
                    : 'border-vibe-border text-vibe-text-secondary hover:bg-vibe-surface-raised',
                  deferred && 'opacity-50 cursor-not-allowed',
                )}
                title={DEFERRED_TITLES[svc] || meta.tooltip}
              >
                <input
                  data-testid={`knowledge-ingest-target-${svc}`}
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleTarget(svc)}
                  disabled={isIngesting || deferred}
                  className="sr-only"
                />
                <span
                  data-testid={`knowledge-ingest-target-dot-${svc}`}
                  className={clsx('inline-block w-2 h-2 rounded-full flex-shrink-0', meta.dotClass)}
                />
                <span>{meta.label}</span>
                {deferred && <Info className="w-3 h-3" />}
              </label>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-vibe-text-secondary">
            Collection
            <span
              data-testid="knowledge-ingest-collection-help"
              title="Logical dataset name. 'docs' maps to {tenant}-docs. Concurrent uploads to a new collection are serialized server-side."
              aria-label="collection help"
              className="text-vibe-text-tertiary cursor-help"
            >
              <Info className="w-3 h-3" />
            </span>
          </label>
          <select
            data-testid="knowledge-ingest-collection"
            value={collection}
            onChange={(e) => setCollection(e.target.value as (typeof COLLECTIONS)[number])}
            disabled={isIngesting}
            className="px-2 py-1 text-xs rounded bg-vibe-bg border border-vibe-border text-vibe-text"
          >
            {COLLECTIONS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <button
            data-testid="knowledge-ingest-submit"
            type="submit"
            disabled={!canSubmit}
            className="ml-auto px-3 py-1.5 text-sm rounded bg-vibe-accent text-vibe-bg disabled:opacity-50"
          >
            {/* R1 C6 lock: in-progress label covers BOTH single-shot and
                folder-batch paths; folder mode shows supported file count
                pre-submit so user sees what they're committing to. */}
            {(isIngesting || isIngestingFolder)
              ? 'Ingesting…'
              : folderMode
                ? `Ingest folder (${folderSupported.length} files)`
                : 'Ingest'}
          </button>
        </div>
      </form>

      {isIngesting && (
        <div
          data-testid="knowledge-ingest-loading"
          className="flex items-center gap-2 text-sm text-vibe-text-secondary py-2 mt-2"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Ingesting into {Array.from(targets).join(', ')}…</span>
        </div>
      )}

      {!isIngesting && ingestError && (
        <div
          data-testid="knowledge-ingest-network-error"
          className="rounded border border-vibe-error/30 bg-vibe-error/10 p-3 text-sm text-vibe-error mt-2"
        >
          Network error: {ingestError.message}
        </div>
      )}

      {!isIngesting && ingestResponse && (
        <div className="mt-3 space-y-2">
          <div className="text-xs font-semibold text-vibe-text-secondary uppercase">
            overall_status: {ingestResponse.overall_status}
          </div>
          {ingestResponse.per_service.map((r) => (
            <ResultCard key={r.service} result={r} />
          ))}
        </div>
      )}
    </section>
  );
}

interface ResultCardProps {
  result: IngestServiceResult;
}

function ResultCard({ result }: ResultCardProps) {
  const statusStyles: Record<IngestStatus, { icon: JSX.Element; cls: string }> = {
    completed: { icon: <CheckCircle2 className="w-4 h-4 text-vibe-success" />, cls: 'border-vibe-success/30 bg-vibe-success/10' },
    partial: { icon: <AlertTriangle className="w-4 h-4 text-vibe-warning" />, cls: 'border-vibe-warning/30 bg-vibe-warning/10' },
    failed: { icon: <XCircle className="w-4 h-4 text-vibe-error" />, cls: 'border-vibe-error/30 bg-vibe-error/10' },
    skipped: { icon: <Info className="w-4 h-4 text-vibe-text-secondary" />, cls: 'border-vibe-text-secondary/30 bg-vibe-text-secondary/10' },
    processing: { icon: <Loader2 className="w-4 h-4 text-vibe-accent animate-spin" />, cls: 'border-vibe-accent/30 bg-vibe-accent/10' },
  };
  const style = statusStyles[result.status];

  return (
    <div
      data-testid={`knowledge-ingest-result-${result.service}`}
      className={`rounded border p-2 ${style.cls}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {style.icon}
          <span className="font-medium text-sm text-vibe-text uppercase">
            {SERVICE_LABELS[result.service]}
          </span>
          <span className="text-xs text-vibe-text-secondary">{result.status}</span>
        </div>
        {result.chunks_created > 0 && (
          <span className="text-xs tabular-nums text-vibe-text-secondary">
            {result.chunks_created} chunks
          </span>
        )}
      </div>
      {result.error && (
        <div className="text-xs text-vibe-text mt-1 break-words">{result.error}</div>
      )}
    </div>
  );
}
